import React from 'react';
import { Grid, Paper, Icon, Typography, Card, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Loader } from '../../elements';
import { ApiService } from '../../hooks';
import { notifications, extractApiErrorMessage, formatDate } from '../../utils';
import { DISPLAY_WEIGHT_UNIT, convertWeight, getWeightUnitLabel } from '../../utils';
import { getAnimalTypeName } from '../../utils/animalutils';
import { LocationStable, LocationStableTranslations } from '../../models/locations';
import { LoaderSize } from '../../constants/constants';
import { useStyles } from './styles';
import {
  HealthSystemAPIControllersAnimalsResponsesAnimalLocationHistoryTimelineResponse,
  HealthSystemDomainAnimalsCommandsLocationHistoryItemViewModel,
  HealthSystemDALEnumsWeightType,
  HealthSystemDomainLocationsResultLocationViewModel
} from '../../api/HealthSystemApi';
import { tokens } from '../../theme/tokens';

interface AnimalLocationHistoryTimelineProps {
  animalId: string;
}

interface ChartDataPoint {
  date: string;
  timestamp: number;
  weight: number | null;
  animalType: number;
  animalTypeName: string;
  location: string;
  type: 'entry' | 'exit';
}

interface AnimalTypeChangeMarker {
  date: string;
  label: string;
}

const AnimalLocationHistoryTimeline: React.FC<AnimalLocationHistoryTimelineProps> = ({ animalId }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [historyData, setHistoryData] =
    React.useState<HealthSystemAPIControllersAnimalsResponsesAnimalLocationHistoryTimelineResponse | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [expandedSteps, setExpandedSteps] = React.useState<Set<string>>(new Set());

  // Always kilograms - the farm's weight type is deliberately not consulted for display.
  const targetWeightUnit = DISPLAY_WEIGHT_UNIT;
  const weightUnitLabel = getWeightUnitLabel(targetWeightUnit, t);

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(stepId)) {
        newExpanded.delete(stepId);
      } else {
        newExpanded.add(stepId);
      }
      return newExpanded;
    });
  };

  const loadLocationHistory = async () => {
    setIsBusy(true);
    setLoaded(false);

    await apiService.api.animals
      .animalsGetAnimalLocationHistoryTimeline({ id: animalId })
      .then((response) => {
        setHistoryData(response.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setLoaded(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    loadLocationHistory();
  }, [animalId]);

  const formatWeight = (value: number | null | undefined, unit: HealthSystemDALEnumsWeightType | undefined): string => {
    if (!value || !unit) return '-';
    const converted = convertWeight(value, unit, targetWeightUnit);
    return converted !== null ? `${converted.toFixed(1)} ${weightUnitLabel}` : '-';
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return formatDate(dateStr);
  };

  const renderLocationName = (location: HealthSystemDomainLocationsResultLocationViewModel): string => {
    const parts: string[] = [];
    if (location.number) parts.push(`#${location.number}`);
    if (location.room) parts.push(`Room: ${location.room}`);
    if (location.pen) parts.push(`Pen: ${location.pen}`);
    return parts.join(' · ') || t('f-location');
  };

  const renderStableName = (location: HealthSystemDomainLocationsResultLocationViewModel): string => {
    return t(LocationStableTranslations[location.stable as LocationStable] ?? 'f-loc-stable-unk');
  };

  const renderFullLocationName = (location: HealthSystemDomainLocationsResultLocationViewModel): string => {
    const parts: string[] = [];
    if (location.farm?.name) parts.push(location.farm.name);
    parts.push(`${t('f-loc-stable')}: ${renderStableName(location)}`);
    if (location.room) parts.push(`${t('f-loc-room')}: ${location.room}`);
    if (location.pen) parts.push(`${t('f-loc-pen')}: ${location.pen}`);
    if (location.number) parts.push(`#${location.number}`);
    return parts.join(', ') || t('f-location');
  };

  const formatDaysLabel = (days: number): string => {
    return days === 1 ? `${days} ${t('f-day-singular')}` : `${days} ${t('f-days-plural')}`;
  };

  const formatDateRange = (enteredAt: string, exitedAt: string | null | undefined): string => {
    const entryDate = formatDateTime(enteredAt);
    const exitDate = exitedAt ? formatDateTime(exitedAt) : null;
    return exitDate ? `${entryDate} – ${exitDate}` : entryDate;
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    if (!historyData?.locationHistory || historyData.locationHistory.length === 0) {
      return { totalLocations: 0, totalDays: 0, averageStay: 0 };
    }

    const totalLocations = historyData.locationHistory.length;
    const totalDays = historyData.locationHistory.reduce((sum, item) => sum + (item.daysInLocation || 0), 0);
    const averageStay = totalLocations > 0 ? totalDays / totalLocations : 0;

    return { totalLocations, totalDays, averageStay };
  };

  const summary = React.useMemo(() => calculateSummary(), [historyData]);

  // Find current location
  const currentLocation = React.useMemo(() => {
    return historyData?.locationHistory?.find((item) => item.isCurrentLocation);
  }, [historyData]);

  // Get past locations (excluding current)
  const pastLocations = React.useMemo(() => {
    return historyData?.locationHistory?.filter((item) => !item.isCurrentLocation) || [];
  }, [historyData]);

  const renderExpandedDetails = (item: HealthSystemDomainAnimalsCommandsLocationHistoryItemViewModel) => (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="font-semibold text-gray-600 mb-3">{t('f-view-details')}</div>

      {/* Grid layout with label on top and value below */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {/* Entry Weight */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">{t('f-entry-weight')}</span>
          <span className="text-sm font-medium text-gray-900">
            {formatWeight(item.weightAtEntry?.value, item.weightAtEntry?.unit)}
          </span>
        </div>

        {/* Exit Weight */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">{t('f-exit-weight')}</span>
          <span className="text-sm font-medium text-gray-900">
            {item.weightAtExit?.value != null && item.weightAtExit.value > 0
              ? formatWeight(item.weightAtExit.value, item.weightAtExit.unit)
              : '-'}
          </span>
        </div>

        {/* Weight Gain */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">{t('f-weight-gain')}</span>
          <span className="text-sm font-medium text-gray-900">
            {item.weightGainGrams !== null && item.weightGainGrams !== undefined
              ? formatWeight(item.weightGainGrams, 1)
              : '-'}
          </span>
        </div>

        {/* Average Daily Gain */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">{t('f-avg-daily-gain')}</span>
          <span className="text-sm font-medium text-gray-900">
            {item.averageDailyGainGrams !== null && item.averageDailyGainGrams !== undefined
              ? `${formatWeight(item.averageDailyGainGrams, 1)} / ${t('f-day')}`
              : '-'}
          </span>
        </div>

        {/* Days in Location */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 mb-1">{t('f-days-in-location')}</span>
          <span className="text-sm font-medium text-gray-900">{item.daysInLocation ?? '-'}</span>
        </div>
      </div>

      {/* Reasons section */}
      {(item.changeReason || item.exitReason) && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          {item.changeReason && (
            <div className="flex flex-col mb-2">
              <span className="text-xs text-gray-500 mb-1">{t('f-change-reason')}</span>
              <span className="text-sm text-gray-700 italic">{item.changeReason}</span>
            </div>
          )}
          {item.exitReason && (
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1">{t('f-exit-reason')}</span>
              <span className="text-sm text-gray-700 italic">{item.exitReason}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const prepareChartData = (): ChartDataPoint[] => {
    if (!historyData?.locationHistory) return [];

    const dataPoints: ChartDataPoint[] = [];

    historyData.locationHistory.forEach((item) => {
      dataPoints.push({
        date: formatDate(item.enteredAt),
        timestamp: new Date(item.enteredAt).getTime(),
        weight: convertWeight(item.weightAtEntry?.value, item.weightAtEntry?.unit, targetWeightUnit),
        animalType: item.animalType,
        animalTypeName: getAnimalTypeName(item.animalType, t),
        location: renderFullLocationName(item.location),
        type: 'entry'
      });

      if (item.exitedAt && item.weightAtExit?.value) {
        dataPoints.push({
          date: formatDate(item.exitedAt),
          timestamp: new Date(item.exitedAt).getTime(),
          weight: convertWeight(item.weightAtExit.value, item.weightAtExit.unit, targetWeightUnit),
          animalType: item.animalType,
          animalTypeName: getAnimalTypeName(item.animalType, t),
          location: renderFullLocationName(item.location),
          type: 'exit'
        });
      }
    });

    return dataPoints.sort((a, b) => a.timestamp - b.timestamp);
  };

  const getAnimalTypeChangeMarkers = (): AnimalTypeChangeMarker[] => {
    if (!historyData?.locationHistory || historyData.locationHistory.length === 0) return [];

    const markers: AnimalTypeChangeMarker[] = [];

    for (let i = 1; i < historyData.locationHistory.length; i++) {
      const current = historyData.locationHistory[i];
      const previous = historyData.locationHistory[i - 1];

      if (current.animalType !== previous.animalType) {
        markers.push({
          date: formatDate(current.enteredAt),
          label: `→ ${getAnimalTypeName(current.animalType, t)}`
        });
      }
    }

    return markers;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      return (
        <Paper className={classes.tooltipPaper}>
          <Typography variant="body2">
            <strong>{data.date}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            {data.location}
          </Typography>
          <Typography variant="body2">
            {t('f-weight')}: {data.weight !== null ? `${data.weight.toFixed(1)} ${weightUnitLabel}` : '-'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {data.animalTypeName} ({data.type === 'entry' ? t('f-entry') : t('f-exit')})
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  const chartData = React.useMemo(() => prepareChartData(), [historyData, targetWeightUnit]);
  const typeChangeMarkers = React.useMemo(() => getAnimalTypeChangeMarkers(), [historyData]);

  const locationHistory = historyData?.locationHistory || [];
  const hasHistory = locationHistory.length > 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {!isBusy && loaded && !hasHistory && (
            <div className={classes.emptyState}>
              <Icon fontSize="large" color="disabled">
                history
              </Icon>
              <Typography variant="h6" color="textSecondary">
                {t('f-no-location-history')}
              </Typography>
            </div>
          )}

          {!isBusy && loaded && hasHistory && (
            <>
              {/* Current Location Block - Sticky & Highlighted */}
              {currentLocation && (
                <div className="sticky top-0 z-10 p-4 mb-6 bg-slate-700 text-white rounded-lg shadow-md">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon style={{ color: 'white' }}>place</Icon>
                    <span className="text-xs font-semibold uppercase tracking-wide">{t('f-current-location')}</span>
                  </div>
                  <div className="text-xl font-medium mb-2">
                    <Link
                      href={`/locations/${currentLocation.location.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      style={{ color: 'white' }}
                    >
                      {renderLocationName(currentLocation.location)}
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-4 opacity-90">
                    <span>
                      {t('f-since')}: {formatDateTime(currentLocation.enteredAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Icon style={{ fontSize: 16, color: 'white' }}>schedule</Icon>
                      <span>{formatDaysLabel(currentLocation.daysInLocation || 0)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">
                      {t('f-loc-stable')}: {renderStableName(currentLocation.location)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">
                      {t('f-com-animal-type')}: {getAnimalTypeName(currentLocation.animalType, t)}
                    </span>
                  </div>
                </div>
              )}

              {/* Location Summary Block */}
              <div className="p-4 mb-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Icon color="action">summarize</Icon>
                  <span className="font-semibold text-gray-600">{t('f-location-summary')}</span>
                </div>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                    <span>
                      {t('f-total-locations')}: {summary.totalLocations}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                    <span>
                      {t('f-total-tracked-days')}: {summary.totalDays}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                    <span>
                      {t('f-average-stay')}: {summary.averageStay.toFixed(1)} {t('f-days-plural')}
                    </span>
                  </li>
                </ul>
              </div>

              {/* Location History Timeline */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Icon color="action">history</Icon>
                  <span className="font-semibold text-gray-600">{t('f-location-history-timeline')}</span>
                </div>

                <div className="space-y-0">
                  {pastLocations.map((item, index) => {
                    const isExpanded = expandedSteps.has(item.id);
                    const isLast = index === pastLocations.length - 1 && !historyData?.isSlaughteredAfterLastExit;

                    return (
                      <div
                        key={item.id}
                        className={`relative pl-8 pb-4 ${!isLast ? 'border-l-2 border-gray-200 ml-2' : 'ml-2'}`}
                      >
                        {/* Timeline Dot */}
                        <div className="absolute left-[-7px] top-0 w-4 h-4 rounded-full bg-slate-600 flex items-center justify-center">
                          <Icon style={{ fontSize: 10, color: 'white' }}>location_on</Icon>
                        </div>

                        {/* Content */}
                        <div className="pt-0">
                          <div className="font-medium text-gray-900 mb-1">
                            {formatDateRange(item.enteredAt, item.exitedAt)}
                          </div>
                          <div className="text-gray-600 mb-1">
                            <Link
                              href={`/locations/${item.location.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              color="inherit"
                            >
                              {renderLocationName(item.location)}
                            </Link>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-1">
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                              {t('f-loc-stable')}: {renderStableName(item.location)}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                              {t('f-com-animal-type')}: {getAnimalTypeName(item.animalType, t)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
                            <Icon style={{ fontSize: 14 }}>schedule</Icon>
                            <span>{formatDaysLabel(item.daysInLocation || 0)}</span>
                          </div>
                          <button
                            onClick={() => toggleStep(item.id)}
                            className="text-slate-600 text-sm hover:underline cursor-pointer bg-transparent border-none p-0 flex items-center gap-1"
                          >
                            <Icon style={{ fontSize: 14 }}>{isExpanded ? 'expand_less' : 'play_arrow'}</Icon>
                            {t('f-view-details')}
                          </button>

                          {isExpanded && renderExpandedDetails(item)}
                        </div>
                      </div>
                    );
                  })}

                  {/* Slaughtered indicator */}
                  {historyData?.isSlaughteredAfterLastExit && (
                    <div className="relative pl-8 pb-4 ml-2">
                      <div className="absolute left-[-7px] top-0 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                        <Icon style={{ fontSize: 10, color: 'white' }}>clear</Icon>
                      </div>
                      <div className="pt-0">
                        <div className="font-medium text-red-600">{t('f-animal-slaughtered')}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Weight Progression Chart */}
              {chartData.length > 0 && (
                <Card className={classes.chartCard}>
                  <Grid container direction="row" alignItems="center" spacing={1} className={classes.chartHeader}>
                    <Grid item>
                      <Icon color="primary">show_chart</Icon>
                    </Grid>
                    <Grid item>
                      <Typography variant="h6">{t('f-animal-weight-progression')}</Typography>
                    </Grid>
                  </Grid>

                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis
                        label={{
                          value: `${t('f-weight')} (${weightUnitLabel})`,
                          angle: -90,
                          position: 'insideLeft'
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="weight"
                        stroke={tokens.primary.main}
                        strokeWidth={2}
                        dot={{ r: 5 }}
                        name={t('f-weight')}
                        connectNulls
                      />

                      {typeChangeMarkers.map((marker, idx) => (
                        <ReferenceLine
                          key={idx}
                          x={marker.date}
                          stroke="#ff9800"
                          strokeDasharray="3 3"
                          label={{ value: marker.label, position: 'top' }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export { AnimalLocationHistoryTimeline };
